import axios from 'axios';
import rateLimit from 'axios-rate-limit';
import events from 'Assets/js/components/events';
import FifoQueue from 'Assets/js/components/fifoQueue';
import globalTheme from 'Assets/js/components/globalTheme';
import phoneHelper from 'Assets/js/components/phoneHelper';
import getCookie from 'Assets/js/components/cookie';
import einstein from 'Assets/js/components/einstein';
import validator from 'Assets/js/components/validator';
import store from 'Assets/js/Vuex/Store/store';
import { map } from 'lodash-es';

// @todo This is set in different modules: isn't it better to do it once, e.g. in main.js?
const http = rateLimit(axios.create(), {
  maxRequests: 6,
  perMilliseconds: 1000,
  maxRPS: 6
});

// @todo Move to service object
function countryCode(setCountryCode) {
  switch (setCountryCode) {
    case 'GB':
      return 'GBP - United Kingdom';
    case 'AU':
      return 'AUD - Australia';
    case 'AT':
      return 'EUR - Austria';
    case 'CH':
      return 'CHF - Switzerland';
    case 'CN':
      return 'CNY - China';
    case 'DE':
      return 'EUR - Germany';
    case 'ES':
      return 'EUR - Spain';
    case 'FR':
      return 'EUR - France';
    case 'IT':
      return 'EUR - Italy';
    case 'JP':
      return 'JPY - Japan';
    case 'NL':
      return 'EUR - Netherlands';
    case 'NZ':
      return 'NZD - New Zealand';
    case 'PT':
      return 'EUR - Portugal';
    case 'SG':
      return 'SGD - Singapore';
    case 'ZA':
      return 'ZAR - South Africa';
    default:
      return 'EUR - Ireland';
  }
}

const triggerErrorLog = (error) => {
  if (typeof window !== 'undefined') {
    const host = window.location?.hostname || '';
    if (
      host.includes('localhost') ||
      host.includes('127.0.0.1') ||
      host.endsWith('.test')
    ) {
      // eslint-disable-next-line no-console
      console.warn('Skipping Teams error log in local/test.', error);
      return;
    }
  }

  axios.post('/wp-json/teams/v1/send', {
    error: { 'service: ': 'userService', error }
  }).catch(() => {});
};

function UserServiceConstructor() {
  const queue = new FifoQueue();
  // Safely resolve settings; globals may not be ready immediately
  const resolveShopifySettings = () => {
    const fromGlobalTheme = (function () {
      try { return globalTheme.shopify(); } catch (e) { return null; }
    })();
    const fromWindow = typeof window !== 'undefined'
      ? window.global_theme_object?.shopify
      : null;
    if (fromGlobalTheme?.url && fromGlobalTheme?.api_version) return fromGlobalTheme;
    if (fromWindow?.url && fromWindow?.api_version) return fromWindow;
    return fromGlobalTheme || fromWindow || {};
  };
  const shopifySettings = resolveShopifySettings();
  const shopifyDomain = (function () {
    try { return globalTheme.siteUrl(); } catch (e) { return null; }
  })() || (typeof window !== 'undefined' ? window.global_theme_object?.site_url : null) || '';
  const url = shopifySettings.url && shopifySettings.api_version
    ? `https://${shopifySettings.url}/api/${shopifySettings.api_version}/graphql.json`
    : null;
  const adminurl = `${globalTheme.siteUrl()}/graphql`;
  const storeFrontAccessToken =
    shopifySettings.store_front_access_token ||
    (typeof window !== 'undefined'
      ? window.global_theme_object?.shopify?.store_front_access_token
      : '');

  // Utilities to tolerate late global initialisation (race on page load)
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const resolveStorefrontUrlNow = () => {
    const s = resolveShopifySettings();
    return s && s.url && s.api_version
      ? `https://${s.url}/api/${s.api_version}/graphql.json`
      : null;
  };
  const waitForStorefrontUrl = async (attempts = 20, interval = 100) => {
    let endpoint = url || resolveStorefrontUrlNow();
    for (let i = 0; i < attempts && !endpoint; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(interval);
      endpoint = resolveStorefrontUrlNow();
    }
    return endpoint;
  };

  const getSession = () => getCookie('STYXKEY_user_session');

  const getSessionEmail = () => getCookie('STYXKEY_user_email');

  /**
   * Show login overlay
   */
  const showLoginOverlay = () => {
    const $loginContainer = document.getElementById('login-container');
    const $loginElements = document.querySelectorAll('.global-search-bar-user');
    $loginElements.forEach(($login) => {
      events.loginActivate();
      $login.classList.toggle('is-active');
      if (!$loginContainer.classList.contains('login-container--active')) {
        $loginContainer.classList.add('login-container--active');
      } else {
        $loginContainer.classList.remove('login-container--active');
      }
    });
  };

  /**
   * Trigger addToWishlist
   * @param sentProductCode
   * @param title
   * @param {Function|undefined} callback
   */
  const triggerAddToWishlist = (
    sentProductCode,
    title,
    callback = undefined
  ) => {
    if (!getSession()) {
      window.dispatchEvent(
        new CustomEvent('error-toggled', {
          detail: {
            subtitle: '',
            title: 'Login or Create Account',
            content: 'Please login or create an account to use the Wishlist',
            button: {
              label: 'Login / Register',
              callback: showLoginOverlay
            }
          }
        })
      );

      return;
    }

    if (!sentProductCode) {
      // eslint-disable-next-line no-console
      console.error('No product code sent');
    }

    let usersOldWishlist = localStorage.getItem('ossUser');
    usersOldWishlist = JSON.parse(usersOldWishlist);
    usersOldWishlist = JSON.stringify(usersOldWishlist.wishlist);

    const data = JSON.stringify({
      query: `mutation customerUpdate($stuff: WishlistAddInput!){
        wishlistAdd(input: $stuff) {
          token
          type
          wishlist{
            value
          }
        }
      }`,
      variables: {
        stuff: {
          token: getSession(),
          productCode: sentProductCode,
          oldWishlist: usersOldWishlist
        }
      }
    });

    const config = {
      method: 'post',
      url: adminurl,
      headers: {
        'Content-Type': 'application/json'
      },
      data
    };

    const formattedTitle = title.split('||')[0].trim();

    queue.push((queueCallback) => {
      http(config).then((response) => {
        if (response.data.errors) {
          triggerErrorLog(response.data.errors);
        }

        if (response.data?.data?.wishlistAdd !== null) {
          const addedHeader = 'Item Added To Wishlist';
          const removedHeader = 'Item Removed From Wishlist';
          const addedResponse = `${formattedTitle} has been added to your Wishlist`;
          const removedResponse = `${formattedTitle} has been removed from your Wishlist`;
          window.dispatchEvent(
            new CustomEvent('error-toggled', {
              detail: {
                subtitle: '',
                title:
                  response.data?.data?.wishlistAdd.type === 'added'
                    ? addedHeader
                    : removedHeader,
                content:
                  response.data?.data?.wishlistAdd.type === 'added'
                    ? addedResponse
                    : removedResponse
              }
            })
          );

          // Trigger event
          if (response.data?.data?.wishlistAdd.type === 'added') {
            events.wishlistAdd(sentProductCode);
          } else {
            events.wishlistRemove(sentProductCode);
          }

          if (typeof callback === 'function') {
            callback({
              type: response.data?.data?.wishlistAdd?.type,
              newWishlist: response.data?.data?.wishlistAdd.wishlist.value
            });
          }
        }
        queueCallback();
      });
    });
  };

  this.getOtherUsers = (companyId, userId) => {
    const data = JSON.stringify({
      query: `query companyUsers($id: String, $except: String){
          companyUsers(id: $id, except: $except) {
            users{
              id
              name
              role
              market
              phone
              email
              address {
                address1
                address2
                city
                postcode
                province
                provinceCode
                country
              }
            }
          }
        }`,
      variables: {
        id: companyId,
        except: userId
      }
    });

    const config = {
      method: 'post',
      url: adminurl,
      headers: {
        'Content-Type': 'application/json'
      },
      data
    };

    return http(config).then((response) => {
      if (response.data.errors) {
        triggerErrorLog(response.data.errors);
      }

      if (response.data?.data?.companyUsers?.users) {
        return response.data?.data?.companyUsers.users;
      }
      return [];
    });
  };

  this.isB2BUser = (userId) => {
    const data = JSON.stringify({
      query: `query b2bUserQuery($user: String){
          b2bUserQuery(input: $user) {
            isMainContact
            groups
            role
            id
            locationId
            companyId
            companyName
            totalOrders
            orders {
                status
                fulfillmentStatus
                checkoutUrl
                cancelledAt
                name
                createdAt
                id
            }
            orderPagination {
                hasNextPage
                endCursor
            }
          }
        }`,
      variables: {
        user: userId
      }
    });

    const config = {
      method: 'post',
      url: adminurl,
      headers: {
        'Content-Type': 'application/json'
      },
      data
    };

    return http(config).then((response) => {
      if (response.data.errors) {
        triggerErrorLog(response.data.errors);
      }
      return response.data?.data?.b2bUserQuery;
    });
  };

  /**
   * @param {String} userId
   * @param {String} cursor
   * @returns {Promise}
   */
  const getB2BUserOrderNextBatch = (userId, cursor) => {
    const data = JSON.stringify({
      query: `query b2bUserOrderQuery($userId: String, $cursor: String){
          b2bUserOrderQuery(userId: $userId, cursor: $cursor) {
            totalOrders
            orders {
                status
                fulfillmentStatus
                checkoutUrl
                cancelledAt
                name
                createdAt
                id
            }
            orderPagination {
                hasNextPage
                endCursor
            }
          }
        }`,
      variables: {
        userId,
        cursor
      }
    });

    const config = {
      method: 'post',
      url: adminurl,
      headers: {
        'Content-Type': 'application/json'
      },
      data
    };

    return http(config).then((response) => {
      if (response.data.errors) {
        triggerErrorLog(response.data.errors);
      }
      return response.data?.data?.b2bUserOrderQuery;
    });
  };

  const login = (user, callback = undefined) => {
    if (!user) {
      // eslint-disable-next-line no-console
      console.error('userService.login failed: invalid user', { user });
      return;
    }

    const data = JSON.stringify({
      query: `mutation customerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
              customerAccessTokenCreate(input: $input) {
                customerAccessToken {
                  accessToken
                  expiresAt
                }
                customerUserErrors {
                  code
                  field
                  message
                }
              }
            }`,
      variables: {
        input: {
          email: user.email,
          password: user.password
        }
      }
    });

    queue.push(async (queueCallback) => {
      const endpoint = await waitForStorefrontUrl();
      if (!endpoint) {
        // eslint-disable-next-line no-console
        console.error('userService.login: storefront URL not available');
        if (typeof callback === 'function') {
          callback({
            customerAccessToken: null,
            customerUserErrors: [{ message: 'Login temporarily unavailable' }]
          });
        }
        queueCallback();
        return;
      }

      const storefrontToken =
        resolveShopifySettings().store_front_access_token ||
        (typeof window !== 'undefined'
          ? window.global_theme_object?.shopify?.store_front_access_token
          : '');
      if (!storefrontToken) {
        // eslint-disable-next-line no-console
        console.warn(
          'userService.getUser: missing Storefront access token'
        );
      }

      const config = {
        method: 'post',
        url: endpoint,
        headers: {
          'X-Shopify-Storefront-Access-Token': storefrontToken,
          'Content-Type': 'application/json'
        },
        data
      };

      http(config).then((response) => {
        if (response.data.errors) {
          triggerErrorLog(response.data.errors);
        }

        if (typeof callback === 'function') {
          callback(response.data.data.customerAccessTokenCreate);
        }
        // Call queue callback
        queueCallback();
      });
    });
  };

  const userModel = (user) => ({
    acceptsMarketing: user.acceptsMarketing,
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    password: user.password,
    phone: (user.phone_prefix ?? '') + user.phone
  });

  const normalizePhone = (user) => {
    const rawPrefix = typeof user?.phone_prefix === 'string' ? user.phone_prefix.trim() : '';
    const rawNumber = typeof user?.phone === 'string' ? user.phone.trim() : '';

    if (!rawPrefix && !rawNumber) {
      return { phone: undefined, error: null };
    }

    let inferredPrefix = rawPrefix;
    if (!inferredPrefix && rawNumber && !rawNumber.startsWith('+')) {
      let countryCode = null;
      const locale = typeof user?.locale === 'string' ? user.locale.trim() : '';
      const explicitCountry =
        typeof user?.country_code === 'string' ? user.country_code.trim() :
        typeof user?.country === 'string' ? user.country.trim() : '';

      if (explicitCountry) {
        countryCode = explicitCountry.toUpperCase();
      } else if (locale) {
        if (/^[A-Za-z]{2}$/.test(locale)) {
          countryCode = locale.toUpperCase();
        } else if (locale.includes('-')) {
          const parts = locale.split('-');
          const tail = parts[parts.length - 1];
          if (/^[A-Za-z]{2}$/.test(tail)) {
            countryCode = tail.toUpperCase();
          }
        } else {
          const markets = globalTheme.markets ? globalTheme.markets() : null;
          if (markets) {
            const match = Object.values(markets).find((m) =>
              typeof m?.name === 'string' &&
              m.name.toLowerCase() === locale.toLowerCase()
            );
            if (match?.country_code) {
              countryCode = String(match.country_code).toUpperCase();
            }
          }
        }
      }

      if (countryCode) {
        inferredPrefix = phoneHelper.prefixFromCountryCode(countryCode) || '';
      }
    }

    let normalized = '';
    if (inferredPrefix) {
      const prefixDigits = inferredPrefix.replace(/[^0-9]/g, '');
      const numberDigits = rawNumber.replace(/[^0-9]/g, '');
      if (!numberDigits) {
        return { phone: null, error: 'Phone is invalid' };
      }
      normalized = `+${prefixDigits}${numberDigits}`;
    } else if (rawNumber.startsWith('+')) {
      const digits = rawNumber.replace(/[^0-9]/g, '');
      if (!digits) {
        return { phone: null, error: 'Phone is invalid' };
      }
      normalized = `+${digits}`;
    } else {
      return { phone: null, error: 'Phone prefix is required' };
    }

    if (!validator.isValidPhoneNumber(normalized)) {
      return { phone: null, error: 'Phone is invalid' };
    }

    return { phone: normalized, error: null };
  };

  function updateMeta(user, updateUserCallback) {
    const validMeta = ['prefix', 'gender', 'locale', 'date_of_birth'];

    const theMeta = {};

    validMeta.forEach((key) => {
      if (!Object.prototype.hasOwnProperty.call(user, key)) {
        return;
      }

      const value = user[key];

      if (value !== null && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'value')) {
        if (typeof value.value === 'string' && value.value !== '') {
          theMeta[key] = value.value;
        }
        return;
      }

      if (typeof value !== 'string' || value === '') {
        return;
      }

      theMeta[key] = value;
    });

    if (Object.keys(theMeta).length === 0) {
      return false;
    }

    theMeta.token = getSession();

    const data = JSON.stringify({
      query: `mutation customerUpdate($meta: MetaInput!){
      meta(input: $meta) {
        token
        success
        error
        meta {
          key
          value
        }
      }
    }`,
      variables: {
        meta: theMeta
      }
    });

    const adminEndpoint = adminurl || (function () {
      try {
        const d = globalTheme.siteUrl ? globalTheme.siteUrl() : (typeof window !== 'undefined' ? window.global_theme_object?.site_url : null);
        return d ? `${d}/graphql` : null;
      } catch (e) {
        return (typeof window !== 'undefined' && window.global_theme_object?.site_url) ? `${window.global_theme_object.site_url}/graphql` : null;
      }
    })();

    if (!adminEndpoint) {
      console.warn('userService.isB2BUser: admin URL not available; returning null');
      return false;
    }

    const config = {
      method: 'post',
      url: adminEndpoint,
      headers: {
        'Content-Type': 'application/json'
      },
      data
    };

    queue.push((queueCallback) => {
      http(config).then((response) => {
        queueCallback();
        updateUserCallback(response);
      });
    });

    return true;
  }


  /**
   * Tidy user meta fields and wishlist
   * @param customer
   * @return {*}
   */
  const tidyUser = (customer) => {
    if (customer) {
      if (customer.gender) {
        customer.gender = customer.gender.value;
      }

      if (customer.wishlist) {
        customer.wishlist = JSON.parse(customer.wishlist.value);
      }

      if (customer.date_of_birth) {
        customer.date_of_birth = customer.date_of_birth.value;
      }

      if (customer.locale) {
        customer.locale = customer.locale.value;
      }

      if (customer.prefix) {
        customer.prefix = customer.prefix.value;
      }
    }

    return customer;
  };

  const updateUser = (user, callback = undefined) => {
    if (!user) {
      return false;
    }

    const updateUserCallback = (metaResponse) => {
      const metaPayload = metaResponse?.data?.data?.meta;
      let metaErrorMessage = null;
      if (!metaPayload) {
        window.dispatchEvent(
          new CustomEvent('error-toggled', {
            detail: {
              subtitle: 'Failure',
              title: 'Updating profile meta details failed',
              content: 'There was an error while updating your profile meta details.'
            }
          })
        );
        metaErrorMessage = 'Meta update failed';
      }

      if (metaPayload?.error) {
        triggerErrorLog(metaPayload.error);
        metaErrorMessage = metaPayload.error;
      }

      const baseUserRaw = userModel(user) || {};
      const phoneResult = normalizePhone(user);
      if (phoneResult.error) {
        if (typeof callback === 'function') {
          callback(undefined, [
            {
              field: ['customer', 'phone'],
              message: phoneResult.error
            }
          ]);
        }
        return;
      }

      if (phoneResult.phone === undefined) {
        delete baseUserRaw.phone;
      } else {
        baseUserRaw.phone = phoneResult.phone;
      }
      const baseUser = Object.keys(baseUserRaw).reduce((acc, key) => {
        const v = baseUserRaw[key];
        if (v !== undefined) {
          acc[key] = v;
        }
        return acc;
      }, {});

      const hasCustomerUpdateFields = Object.keys(baseUser).length > 0;
      const sessionId = getSession();

      if (!hasCustomerUpdateFields) {
        window.dispatchEvent(
          new CustomEvent('error-toggled', {
            detail: {
              subtitle: 'Updated',
              title: 'Profile details updated',
              content: 'Your profile details have been updated.'
            }
          })
        );

        if (typeof callback === 'function') {
          callback(undefined, []);
        }

        return;
      }

      const data = JSON.stringify({
        query: `mutation customerUpdate($customer: CustomerUpdateInput!, $customerAccessToken: String!) {
        customerUpdate(customer: $customer, customerAccessToken: $customerAccessToken) {
          customer {
            id
            prefix: metafield(namespace: "custom", key: "prefix") { value }
            firstName
            lastName
            acceptsMarketing
            email
            phone
            orders(first: 5) {
              edges {
                node {
                  id
                  orderNumber
                  totalPriceV2 { amount currencyCode }
                  customerLocale
                  fulfillmentStatus
                  successfulFulfillments {
                    trackingCompany
                    trackingInfo { number url }
                  }
                  lineItems(first: 20) {
                    edges {
                      node {
                        variant {
                          id
                          product {
                            id
                            title
                            description
                            featuredImage { src }
                            handle
                            totalInventory
                          }
                        }
                        quantity
                        title
                        customAttributes { value key }
                      }
                    }
                  }
                }
              }
            }
            gender: metafield(namespace: "custom", key: "gender") { value }
            date_of_birth: metafield(namespace: "custom", key: "date_of_birth") { value }
            locale: metafield(namespace: "custom", key: "locale") { value }
            wishlist: metafield(namespace: "my_fields", key: "wishlist") { value }
            addresses(first: 20) {
              edges {
                node {
                  id
                  firstName
                  lastName
                  address1
                  address2
                  city
                  province
                  zip
                  country
                }
              }
            }
            defaultAddress {
              id
              firstName
              lastName
              address1
              address2
              city
              province
              zip
              country
              countryCodeV2
            }
          }
          customerUserErrors { code field message }
        }
      }`,
        variables: {
          customer: baseUser,
          customerAccessToken: sessionId
        }
      });

      waitForStorefrontUrl().then((endpoint) => {
        if (!endpoint) {
          // eslint-disable-next-line no-console
          console.error('userService.updateUser: storefront URL not available');
          if (typeof callback === 'function') {
            callback(undefined, [{ message: 'Update temporarily unavailable' }]);
          }
          return;
        }

        const storefrontToken =
          resolveShopifySettings().store_front_access_token ||
          (typeof window !== 'undefined'
            ? window.global_theme_object?.shopify?.store_front_access_token
            : '');

        if (!storefrontToken) {
          // eslint-disable-next-line no-console
          console.warn('userService.updateUser: missing Storefront access token');
        }

        const config = {
          method: 'post',
          url: endpoint,
          headers: {
            'X-Shopify-Storefront-Access-Token': storefrontToken,
            'Content-Type': 'application/json'
          },
          data
        };

        http(config)
          .then((response) => {
          if (response?.data?.errors) {
            triggerErrorLog(response.data.errors);
          }

          const userErrors = response?.data?.data?.customerUpdate?.customerUserErrors || [];
          if (metaErrorMessage) {
            userErrors.push({ message: metaErrorMessage });
          }

          if (userErrors.length) {
            window.dispatchEvent(
              new CustomEvent('error-toggled', {
                detail: {
                  subtitle: 'Failure',
                  title: 'Updating profile details failed',
                  content: 'There was an error while updating your profile details.'
                }
              })
            );
          } else if (response?.data?.data?.customerUpdate?.customer) {
            window.dispatchEvent(
              new CustomEvent('error-toggled', {
                detail: {
                  subtitle: 'Updated',
                  title: 'Profile details updated',
                  content: 'Your profile details have been updated.'
                }
              })
            );
          }

            if (typeof callback === 'function') {
              let customer = response?.data?.data?.customerUpdate?.customer;
              if (customer) {
                customer = tidyUser(customer);
              }
              callback(customer, userErrors);
            }
          })
          .catch((err) => {
          triggerErrorLog(err);

          window.dispatchEvent(
            new CustomEvent('error-toggled', {
              detail: {
                subtitle: 'Failure',
                title: 'Updating profile details failed',
                content: 'There was an error while updating your profile details.'
              }
            })
          );

            if (typeof callback === 'function') {
              callback(undefined, [{ message: 'Request failed' }]);
            }
          });
      });
    };

    const didQueueMetaUpdate = updateMeta(user, updateUserCallback);

    if (!didQueueMetaUpdate) {
      updateUserCallback({
        data: {
          data: {
            meta: {
              success: true,
              error: null
            }
          }
        }
      });
    }

    return true;
  };

  const logout = () => {
    document.cookie =
      'STYXKEY_shopify_defined_locale=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie =
      'STYXKEY_user_session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie =
      'STYXKEY_user_authed=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie =
      'STYXKEY_user_email=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie =
      'STYXKEY_user_cart=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

    localStorage.removeItem('ossUser');
  };

  /**
   * Get user from Shopify API
   * @param cartService
   * @param {Function|undefined} callback
   * @param {Function|undefined} failedCallback
   */
  const getUser = (
    cartService,
    callback = undefined,
    failedCallback = undefined
  ) => {
    const sessionId = getSession();

    if (!sessionId) {
      console.warn('userService.getUser failed, session is empty');
      if (typeof failedCallback === 'function') {
        failedCallback(null);
      }
      return;
    }

    cartService.addUserToCart(sessionId, (response) => {
      if (response.cart.buyerIdentity.countryCode) {
        document.cookie = `STYXKEY_locale=${countryCode(
          response.cart.buyerIdentity.countryCode
        )}; expires=Session; path=/;`;
      }
    });

    const data = JSON.stringify({
      query: `query {
                customer( customerAccessToken: "${sessionId}") {
                  id
                  prefix: metafield(namespace: "custom", key: "prefix") {
                     value
                  }
                  firstName
                  lastName
                  acceptsMarketing
                  email
                  phone
                  orders(first: 5) {
                    edges {
                      node {
                        id
                        orderNumber
                        totalPriceV2{
                          amount
                          currencyCode
                        }
                        customerLocale
                        fulfillmentStatus
                        successfulFulfillments{
                          trackingCompany
                          trackingInfo{
                            number
                            url
                          }
                        }
                        lineItems(first: 20){
                          edges {
                            node {
                              variant {
                                id
                                product{
                                  id
                                  title
                                  description
                                  featuredImage{
                                    src
                                  }
                                  handle
                                  totalInventory
                                }
                              }
                              quantity
                              title
                              customAttributes{
                                value
                                key
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                  gender: metafield(namespace: "custom", key: "gender") {
                     value
                  }
                  date_of_birth: metafield(namespace: "custom", key: "date_of_birth") {
                     value
                  }
                  locale: metafield(namespace: "custom", key: "locale") {
                      value
                  }
                  wishlist: metafield(namespace: "my_fields", key: "wishlist") {
                     value
                  }
                  addresses(first: 20) {
                    edges{
                      node{
                        id
                        firstName
                        lastName
                        address1
                        address2
                        city
                        province
                        zip
                        country
                      }
                    }
                  }
                  defaultAddress {
                    id
                    firstName
                    lastName
                    address1
                    address2
                    city
                    province
                    zip
                    country
                    countryCodeV2
                  }
                }
            }`
    });

    queue.push(async (queueCallback) => {
      const endpoint = await waitForStorefrontUrl();
      if (!endpoint) {
        // eslint-disable-next-line no-console
        console.error('userService.getUser: storefront URL not available');
        if (typeof callback === 'function') {
          callback(null);
        }
        queueCallback();
        return;
      }

      const config = {
        method: 'post',
        url: endpoint,
        headers: {
          'X-Shopify-Storefront-Access-Token': storeFrontAccessToken,
          'Content-Type': 'application/json'
        },
        data
      };

      function tidyUserLocal(response) {
        if (response.data.data.customer) {
          response.data.data.customer = tidyUser(response.data.data.customer);

          if (response.data.data.customer.defaultAddress) {
            document.cookie =
              'STYXKEY_shopify_defined_locale=true; expires=Session; path=/;';
            document.cookie = `STYXKEY_locale=${countryCode(
              response.data.data.customer.defaultAddress.countryCodeV2
            )}; expires=Session; path=/;`;
          }
        } else {
          // If customer is null, not existing in Shopify
          // Delete cookie
          logout();
        }

        return response;
      }

      http(config)
        .then((response) => {
          if (response?.data?.errors) {
            triggerErrorLog(response.data.errors);
          }

          response = tidyUserLocal(response);
          return response?.data?.data?.customer || null;
        })
        .then((responseData) => {
          // If no customer or no id, skip B2B lookup and continue with empty object
          if (!responseData || !responseData.id) {
            return responseData || {};
          }

          return this.isB2BUser(responseData.id)
            .then((isB2BUser) => {
              responseData.b2b = isB2BUser;
              return responseData;
            })
            .catch(() => responseData); // fallback silently on B2B lookup failure
        })
        .then((responseDataFinal) => {
          if (typeof callback === 'function') {
            callback(responseDataFinal || null);
          }
          queueCallback();
        })
        .catch((err) => {
          // Fallback on any unexpected error to avoid breaking the UI
          // eslint-disable-next-line no-console
          console.error('userService.getUser failed', err);
          if (typeof callback === 'function') {
            callback(null);
          }
          queueCallback();
        });
    });
  };

  const getUserB2BInfo = (responseDataWithB2b, callback = undefined) => {
    // If no callback, just pass-through
    if (typeof callback !== 'function') {
      return;
    }

    // If no B2B or not a location admin, just pass-through
    if (
      !responseDataWithB2b ||
      !responseDataWithB2b.b2b ||
      responseDataWithB2b.b2b?.role !== 'location_admin'
    ) {
      callback(responseDataWithB2b);
      return;
    }

    this.getOtherUsers(
      responseDataWithB2b.b2b.companyId,
      responseDataWithB2b.b2b.id
    )
      .then((otherUsers) => {
        responseDataWithB2b.b2bUserList = otherUsers;
        callback(responseDataWithB2b);
      })
      .catch(() => responseDataWithB2b);
  };

  const register = (user, callback = undefined) => {
    if (!user) {
      // eslint-disable-next-line no-console
      console.error('userService.register failed: invalid user', { user });
      return;
    }

    const data = JSON.stringify({
      query: `mutation customerCreate {
            customerCreate(
                input: {
                    email: "${user.email}"
                    firstName: "${user.firstname}"
                    lastName: "${user.lastname}"
                    password: "${user.password}"
                }
            ){
              userErrors {
                field
                message
              }
              customer {
                id
              }
            }
          }`
    });

    const endpoint = (function () {
      try {
        const s = globalTheme.shopify ? globalTheme.shopify() : (typeof window !== 'undefined' ? window.global_theme_object?.shopify : null);
        return s && s.url && s.api_version ? `https://${s.url}/api/${s.api_version}/graphql.json` : null;
      } catch (e) { return (typeof window !== 'undefined' && window.global_theme_object?.shopify && window.global_theme_object.shopify.url && window.global_theme_object.shopify.api_version)
        ? `https://${window.global_theme_object.shopify.url}/api/${window.global_theme_object.shopify.api_version}/graphql.json`
        : null; }
    })();

    if (!endpoint) {
      // eslint-disable-next-line no-console
      console.error('userService.login: storefront URL not available');
      queue.push((queueCallback) => {
        if (typeof callback === 'function') {
          callback({
            customerAccessToken: null,
            customerUserErrors: [{ message: 'Login temporarily unavailable' }]
          });
        }
        queueCallback();
      });
      return;
    }

    const config = {
      method: 'post',
      url: endpoint,
      headers: {
        'X-Shopify-Storefront-Access-Token': storeFrontAccessToken,
        'Content-Type': 'application/json'
      },
      data
    };

    queue.push((queueCallback) => {
      http(config).then((response) => {
        if (response.data.errors) {
          triggerErrorLog(response.data.errors);
        }

        if (typeof callback === 'function') {
          callback(response.data);
        }
        queueCallback();
      });
    });
  };

  const setSession = (access, email) => {
    if (!access || !access.accessToken) {
      // eslint-disable-next-line no-console
      console.error('userService.setSession failed: no access token', {
        access
      });
      return;
    }

    const expires = new Date(access.expiresAt).toUTCString();
    document.cookie = `STYXKEY_user_session=${access.accessToken}; expires=${expires}; path=/`;
    document.cookie = `STYXKEY_user_authed=1; expires=${expires}; path=/`;
    document.cookie = `STYXKEY_user_email=${email}; expires=${expires}; path=/`;
  };

  const loginEnabled = () => {};

  const addressModel = (address) => ({
    address1: address.address1,
    address2: address.address2,
    city: address.city,
    country: address.country,
    firstName: address.firstName,
    lastName: address.lastName,
    province: address.province,
    zip: address.zip
  });

  function addNewAddress(addressId, newAddress, callback) {
    const sessionId = getSession();
    const baseAddress = addressModel(newAddress);

    const data = JSON.stringify({
      query: `mutation customerAddressCreate($address: MailingAddressInput!, $customerAccessToken: String!) {
              customerAddressCreate(
                address: $address,
                customerAccessToken: $customerAccessToken
              ){
                  customerAddress {
                    id
                    firstName
                    lastName
                    company
                    address1
                    address2
                    city
                    province
                    zip
                  }
                  customerUserErrors {
                    code
                    field
                    message
                  }
              }}`,
      variables: {
        address: baseAddress,
        customerAccessToken: sessionId
      }
    });

    const config = {
      method: 'post',
      url,
      headers: {
        'X-Shopify-Storefront-Access-Token': storeFrontAccessToken,
        'Content-Type': 'application/json'
      },
      data
    };

    http(config).then((response) => {
      if (response.data.errors) {
        triggerErrorLog(response.data.errors);
      }

      if (typeof callback === 'function') {
        // eslint-disable-next-line no-console
        console.log('response for adding new address ', response);
        callback(response);
      }
    });
  }

  const updateAddress = (addressId, newAddress, callback = undefined) => {
    if (!addressId) {
      addNewAddress(addressId, newAddress, callback);
      return;
    }

    const sessionId = getSession();
    const baseAddress = addressModel(newAddress);

    const data = JSON.stringify({
      query: `mutation customerAddressUpdate($address: MailingAddressInput!, $customerAccessToken: String!, $id: ID!) {
              customerAddressUpdate(
                id: $id
                address: $address,
                customerAccessToken: $customerAccessToken
              ){
                  customerAddress {
                    id
                    firstName
                    lastName
                    company
                    address1
                    address2
                    city
                    province
                    zip
                  }
                  customerUserErrors {
                    code
                    field
                    message
                  }
              }}`,
      variables: {
        id: addressId,
        address: baseAddress,
        customerAccessToken: sessionId
      }
    });

    const config = {
      method: 'post',
      url,
      headers: {
        'X-Shopify-Storefront-Access-Token': storeFrontAccessToken,
        'Content-Type': 'application/json'
      },
      data
    };

    http(config).then((response) => {
      if (response.data.errors) {
        triggerErrorLog(response.data.errors);
      }

      if (typeof callback === 'function') {
        // eslint-disable-next-line no-console
        console.log('response for address ', response);
        callback(response);
      }
    });
  };

  const removeAddress = (addressId, callback) => {
    if (!addressId) {
      // eslint-disable-next-line no-console
      console.error('userService.removeAddress failed: no user address', {
        addressId
      });
      return;
    }

    const sessionId = getSession();

    // console.log('userService.removeAddress', {addressId, sessionId});

    const data = JSON.stringify({
      query: `mutation customerAddressDelete {
            customerAddressDelete(
                customerAccessToken: "${sessionId}"
                id: "${addressId}"
            ){
              userErrors {
                field
                message
              }
              deletedCustomerAddressId
            }
          }`
    });

    const config = {
      method: 'post',
      url,
      headers: {
        'X-Shopify-Storefront-Access-Token': storeFrontAccessToken,
        'Content-Type': 'application/json'
      },
      data
    };

    http(config)
      .then(callback)
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('userService.removeAddress failed', { err });
      });
  };

  const forgotPassword = (sentDetails, callback = undefined) => {
    if (!sentDetails || !sentDetails.email) {
      // eslint-disable-next-line no-console
      console.error('userService.forgotPassword failed: invalid details', {
        sentDetails
      });
      return;
    }

    const data = JSON.stringify({
      query: `mutation customerRecover($email: String!) {
              customerRecover(email: $email) {
                customerUserErrors {
                  code
                  field
                  message
                }
              }
            }`,
      variables: {
        email: sentDetails.email
      }
    });

    const endpoint = (function () {
      try {
        const s = globalTheme.shopify ? globalTheme.shopify() : (typeof window !== 'undefined' ? window.global_theme_object?.shopify : null);
        return s && s.url && s.api_version ? `https://${s.url}/api/${s.api_version}/graphql.json` : null;
      } catch (e) { return (typeof window !== 'undefined' && window.global_theme_object?.shopify && window.global_theme_object.shopify.url && window.global_theme_object.shopify.api_version)
        ? `https://${window.global_theme_object.shopify.url}/api/${window.global_theme_object.shopify.api_version}/graphql.json`
        : null; }
    })();

    if (!endpoint) {
      // eslint-disable-next-line no-console
      console.error('userService.register: storefront URL not available');
      queue.push((queueCallback) => {
        if (typeof callback === 'function') {
          callback({
            errors: [{ message: 'Registration temporarily unavailable' }]
          });
        }
        queueCallback();
      });
      return;
    }

    const config = {
      method: 'post',
      url: endpoint,
      headers: {
        'X-Shopify-Storefront-Access-Token': storeFrontAccessToken,
        'Content-Type': 'application/json'
      },
      data
    };

    queue.push((queueCallback) => {
      http(config).then((response) => {
        if (response.data.errors) {
          triggerErrorLog(response.data.errors);
        }

        if (typeof callback === 'function') {
          callback(response.data);
        }
        queueCallback();
      });
    });
  };

  /**
   * @param {Object} payload
   * @param {Function|Undefined} callback
   */
  const addsB2BContact = (payload, callback = undefined) => {
    const data = JSON.stringify({
      query: `mutation registerB2bUser($input: RegisterB2bUserInput!){
                  registerB2bUser( input: $input ){
                      contactId,
                      market,
                      error
                  }
              }`,
      variables: {
        input: {
          token: '12345',
          companyId: payload.companyId,
          userDetails: payload.user
        }
      }
    });

    const config = {
      method: 'post',
      url: adminurl,
      headers: {
        'Content-Type': 'application/json'
      },
      data
    };

    queue.push((queueCallback) => {
      http(config).then((response) => {
        const responseErrors = response.data?.errors;
        if (responseErrors) {
          triggerErrorLog(responseErrors);
        }

        if (typeof callback === 'function') {
          const registerResult = response.data?.data?.registerB2bUser;
          let error = null;
          const debugMessage = responseErrors?.[0]?.extensions?.debugMessage;
          const errorMessage = responseErrors?.[0]?.message;

          if (!registerResult) {
            error = errorMessage || 'Register B2B user failed.';
            // eslint-disable-next-line no-console
            console.warn('[USER SERVICE] registerB2bUser missing response data');
          } else if (registerResult?.error !== null) {
            error = registerResult?.error;
          }

          if (!error && debugMessage) {
            try {
              const start = debugMessage.indexOf('[');
              const end = debugMessage.lastIndexOf(']');
              if (start !== -1 && end !== -1 && end > start) {
                const parsed = JSON.parse(debugMessage.slice(start, end + 1));
                if (Array.isArray(parsed) && parsed.length) {
                  error = parsed.join(', ');
                }
              }
              if (!error) {
                error = debugMessage;
              }
            } catch (e) {
              error = debugMessage;
            }
          }

          if (error) {
            window.dispatchEvent(
              new CustomEvent('error-toggled', {
                detail: {
                  subtitle: '',
                  title: 'Error',
                  content: error
                }
              })
            );
          }

          callback({ data: response.data, error, registerResult });
        }
        queueCallback();
      });
    });
  };

  this.removeB2bUserPrivs = async (payload) => {
    const data = JSON.stringify({
      query: `mutation removeB2bUserPermissions($input: RemoveB2bUserPermissionsInput!){
                            removeB2bUserPermissions(input: $input) {
                                status
                                error
                            }
                        }`,
      variables: {
        input: {
          id: payload
        }
      }
    });

    const config = {
      method: 'post',
      url: adminurl,
      headers: {
        'Content-Type': 'application/json'
      },
      data
    };

    try {
      const response = await http(config);
      if (response.data.data.removeB2bUserPermissions.status === 'success') {
        return true;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      return false;
    }
  };

  this.detachB2bUser = (payload, callback = undefined) => {
    const data = JSON.stringify({
      query: `mutation removeB2bUser($input: RemoveB2bUserInput!){
                removeB2bUser(input: $input) {
                    deletedCompanyContactId
                    error
                }
            }`,
      variables: {
        input: {
          id: payload
        }
      }
    });

    const config = {
      method: 'post',
      url: adminurl,
      headers: {
        'Content-Type': 'application/json'
      },
      data
    };

    queue.push((queueCallback) => {
      http(config).then((response) => {
        if (response.data.errors) {
          triggerErrorLog(response.data.errors);
        }

        if (typeof callback === 'function') {
          if (response.data.data?.removeB2bUser.error) {
            // eslint-disable-next-line no-console
            console.error(
              'Remove B2B user error: ',
              response.data.data?.removeB2bUser.error
            );
          }

          if (
            response.data.data?.removeB2bUser?.error ===
            'Cannot delete a company contact with existing orders or draft orders.'
          ) {
            window.dispatchEvent(
              new CustomEvent('error-toggled', {
                detail: {
                  subtitle: 'Error',
                  title: 'User has orders',
                  content:
                    'This contact has existing orders or draft orders, are you sure you want to remove them?',
                  button: {
                    label: 'Confirm',
                    callback: () => {
                      this.removeB2bUserPrivs(payload).then((res) => {
                        if (res) {
                          store
                            .dispatch('userRefresh')
                            .then(() => {
                              // eslint-disable-next-line no-console
                              console.log('did user init, run detach again...');
                              this.detachB2bUser(payload, callback);
                            });
                        }
                      });
                    }
                  }
                }
              })
            );
          }

          if (
            response.data.data?.removeB2bUser.error !== null &&
            response.data.data?.removeB2bUser.error !==
              'Cannot delete a company contact with existing orders or draft orders.'
          ) {
            window.dispatchEvent(
              new CustomEvent('error-toggled', {
                detail: {
                  subtitle: '',
                  title: 'Error',
                  content: response.data.data.removeB2bUser.error,
                  button: {
                    label: null
                  }
                }
              })
            );
          }
          callback(response.data);
        }
        queueCallback();
      });
    });
  };

  const queryDetachB2bUser = (payload, callback = undefined) => {
    window.dispatchEvent(
      new CustomEvent('error-toggled', {
        detail: {
          subtitle: 'B2b Contact',
          title: `Are you sure you want to remove ${payload.name}?`,
          content: `Are you sure you want to remove ${payload.name}?<br>They will still be able to access their account.`,
          button: {
            label: 'Remove',
            callback: () => {
              this.detachB2bUser(payload.id, callback || payload.callback);
            }
          }
        }
      })
    );
  };

  /**
   * @param userId
   * @param {Function|undefined} callback
   */

  // Not sure this is valid?
  const getB2BUser = (userId, callback = undefined) => {
    const data = JSON.stringify({
      query: `query companyRecepient($user: String){
          companyRecepient(id: $user) {
            company
            id
            firstName
            lastName
            email
            phone
            defaultAddress {
              address1
              address2
              city
              postcode
              country
            }
          }
        }`,
      variables: {
        user: userId
      }
    });

    const config = {
      method: 'post',
      url: adminurl,
      headers: {
        'Content-Type': 'application/json'
      },
      data
    };

    queue.push((queueCallback) => {
      http(config).then((response) => {
        if (response.data.errors) {
          triggerErrorLog(response.data.errors);
        }

        if (typeof callback === 'function') {
          callback(response.data);
        }
        queueCallback();
      });
    });
  };

  const updateB2BContact = (payload, callback = undefined) => {
    const data = JSON.stringify({
      query: `mutation updateB2bUser($input: UpdateB2bUserInput!){
                  updateB2bUser( input: $input ){
                      contactId,
                      error
                  }
              }`,
      variables: {
        input: {
          id: payload.id,
          userDetails: payload.user
        }
      }
    });

    const config = {
      method: 'post',
      url: adminurl,
      headers: {
        'Content-Type': 'application/json'
      },
      data
    };

    queue.push((queueCallback) => {
      http(config).then((response) => {
        if (typeof callback === 'function') {
          if (response.data.data.updateB2bUser.error !== null) {
            window.dispatchEvent(
              new CustomEvent('error-toggled', {
                detail: {
                  subtitle: '',
                  title: 'Error',
                  content: response.data.data.updateB2bUser.error
                }
              })
            );
          }
          callback(response.data);
        }
        queueCallback();
      });
    });
  };

  const forceRecommendProduct = (id, callback = undefined) => {
    const prefix = 'gid://shopify/Product/';

    let theId = id;

    if (!id.startsWith(prefix)) {
      theId = prefix + id;
    }

    const data = JSON.stringify({
      query: `query products{
          products(with_shopify_id: "${theId}", limit: 1, allow_substitutes: false){
              wordpress_id
              shopify_id
              shopify_title
              brand
              hasOnlyDefaultVariant
              wordpress_slug
              description
              hasOutOfStockVariants
              market_prices(country_code: "${globalTheme.localCode()}"){
                country_code
                price_formatted_plus_vat
                compare_at_price_formatted_plus_vat
                is_discounted
              }
              featuredImage{
                  src
                  alt_text
              }
              images(first: 1){
                src
                alt_text
              }
              shopify_metafields{
                requires_personalisation
                personalisation_limit
                allowed_for_b2c
                allowed_for_b2b
                engraving_preview_image{
                  url
                  width
                  height
                }
                engraving_preview_font
                engraving_preview_font_size
                engraving_preview_font_color
                engraving_preview_font_opacity
                engraving_text_position_adjustmentx
                engraving_text_position_adjustmenty
                engraving {
                  available
                  product
                }
                notify_me_block
                product
              }
              tags{
                priority
                random
                all
              }
              product_variants{
                  id
                  availableForSale
              }
              product_reviews{
                  aggregate_summary{
                        stars_average_rounded_to_fives
                        number_of_reviews{
                          total
                        }
                  }
              }
          }
        }`
    });

    const config = {
      method: 'post',
      url: adminurl,
      headers: {
        'Content-Type': 'application/json'
      },
      data
    };

    http(config).then((response) => {
      if (response.data.errors) {
        triggerErrorLog(response.data.errors);
      }

      if (
        response.data?.data?.products !== null &&
        typeof callback === 'function'
      ) {
        callback(response.data?.data?.products);
      }
    });
  };

  const recommendProducts = (callback = undefined) => {
    const data = JSON.stringify({
      query: `query products{
          products(limit: 8, allow_substitutes: true, dont_allow: ["enquire_now"], shuffle: true){
              wordpress_id
              shopify_id
              shopify_title
              brand
              hasOnlyDefaultVariant
              wordpress_slug
              description
              hasOutOfStockVariants
              market_prices(country_code: "${globalTheme.localCode()}"){
                country_code
                price_formatted_plus_vat
                compare_at_price_formatted_plus_vat
                is_discounted
              }
              featuredImage{
                  src
                  alt_text
              }
              images(first: 1){
                src
                alt_text
              }
              shopify_metafields{
                requires_personalisation
                personalisation_limit
                allowed_for_b2c
                allowed_for_b2b
                engraving_preview_image{
                  url
                  width
                  height
                }
                engraving_preview_font
                engraving_preview_font_size
                engraving_preview_font_color
                engraving_preview_font_opacity
                engraving_text_position_adjustmentx
                engraving_text_position_adjustmenty
                engraving {
                  available
                  product
                }
                notify_me_block
                product
              }
              tags{
                priority
                random
                all
              }
              product_variants{
                  id
                  availableForSale
              }
              product_reviews{
                  aggregate_summary{
                        stars_average_rounded_to_fives
                        number_of_reviews{
                          total
                        }
                  }
              }
          }
        }`
    });

    const config = {
      method: 'post',
      url: adminurl,
      headers: {
        'Content-Type': 'application/json'
      },
      data
    };

    http(config).then((response) => {
      if (response.data.errors) {
        triggerErrorLog(response.data.errors);
      }

      if (
        response.data?.data?.products !== null &&
        typeof callback === 'function'
      ) {
        console.log('PRODUCTNs FOR REC:', response.data?.data?.products);
        callback(response.data?.data?.products);
      }
    });
  };

  const einsteinProductRecommendations = (callback = undefined) => {
    const processResponse = (items) => {
      const jsonEncoded = JSON.stringify(items);
      const data = JSON.stringify({
        query: `query products{
          products(with_shopify_ids: ${jsonEncoded}, allow_substitutes: true, dont_allow: ["enquire_now"], limit: 8, shuffle: false, use_original_order: true){
              wordpress_id
              shopify_id
              shopify_title
              brand
              hasOnlyDefaultVariant
              wordpress_slug
              description
              hasOutOfStockVariants
              market_prices(country_code: "${globalTheme.localCode()}"){
                country_code
                price_formatted_plus_vat
                compare_at_price_formatted_plus_vat
                is_discounted
              }
              featuredImage{
                  src
                  alt_text
              }
              images(first: 1){
                src
                alt_text
              }
              shopify_metafields{
                requires_personalisation
                personalisation_limit
                allowed_for_b2c
                allowed_for_b2b
                engraving_preview_image{
                  url
                  width
                  height
                }
                engraving_preview_font
                engraving_preview_font_size
                engraving_preview_font_color
                engraving_preview_font_opacity
                engraving_text_position_adjustmentx
                engraving_text_position_adjustmenty
                engraving {
                  available
                  product
                }
                notify_me_block
                product
              }
              tags{
                priority
                random
                all
              }
              product_variants{
                  id
                  availableForSale
              }
              product_reviews{
                  aggregate_summary{
                        stars_average_rounded_to_fives
                        number_of_reviews{
                          total
                        }
                  }
              }
              market_exclusives
          }
        }`
      });

      const config = {
        method: 'post',
        url: adminurl,
        headers: {
          'Content-Type': 'application/json'
        },
        data
      };

      http(config).then((products) => {
        if (products.data.errors) {
          triggerErrorLog(products.data.errors);
        }

        if (
          products.data?.data?.products !== null &&
          typeof callback === 'function'
        ) {
          callback(products.data?.data?.products);
        }
      });
    };

    einstein.getRecommendations(
      globalTheme.einsteinRecs(),
      (response) => {
        processResponse(
          map(response.data[0].items, (item) => item.product_code)
        );
      },
      () => {
        // eslint-disable-next-line no-console
        console.warn('Retrieving fallback Einstein Recommendations.');
        recommendProducts((response) => {
          processResponse(
            map(response, (item) =>
              item.shopify_id.replace('gid://shopify/Product/', '')
            )
          );
        });
      }
    );
  };

  const einsteinCartRecommendations = (callback = undefined) => {
    const processResponse = (items) => {
      const jsonEncoded = JSON.stringify(items);
      const data = JSON.stringify({
        // Todo, rip this out.
        query: `query products{
          products(with_shopify_ids: ${jsonEncoded}, allow_substitutes: true, dont_allow: ["enquire_now"], limit: 3, shuffle: false, use_original_order: true){
              wordpress_id
              shopify_id
              shopify_title
              brand
              hasOnlyDefaultVariant
              wordpress_slug
              description
              hasOutOfStockVariants
              market_prices(country_code: "${globalTheme.localCode()}"){
                country_code
                price_formatted_plus_vat
                compare_at_price_formatted_plus_vat
                is_discounted
              }
              featuredImage{
                  src
                  alt_text
              }
              images(first: 1){
                src
                alt_text
              }
              shopify_metafields{
                requires_personalisation
                personalisation_limit
                allowed_for_b2c
                allowed_for_b2b
                engraving {
                  available
                  product
                }
                notify_me_block
                product
              }
              tags{
                priority
                random
                all
              }
              product_variants{
                  id
                  availableForSale
              }
              product_reviews{
                  aggregate_summary{
                        stars_average_rounded_to_fives
                        number_of_reviews{
                          total
                        }
                  }
              }
              market_exclusives
          }
        }`
      });

      const config = {
        method: 'post',
        url: adminurl,
        headers: {
          'Content-Type': 'application/json'
        },
        data
      };

      http(config).then((products) => {
        if (products.data.errors) {
          triggerErrorLog(products.data.errors);
        }

        if (
          products.data?.data?.products !== null &&
          typeof callback === 'function'
        ) {
          callback(products.data?.data?.products);
        }
      });
    };

    einstein.getRecommendations(
      globalTheme.einsteinCart(),
      (response) => {
        processResponse(
          map(response.data[0].items, (item) => item.product_code)
        );
      },
      () => {
        // eslint-disable-next-line no-console
        console.warn('Retrieving fallback Einstein Recommendations.');
        recommendProducts((response) => {
          processResponse(
            map(response, (item) =>
              item.shopify_id.replace('gid://shopify/Product/', '')
            )
          );
        });
      }
    );
  };

  /**
   * Register B2B company
   * @param {Object} payload
   * @param {Function|undefined} callback
   */
  const registerB2bCompany = (payload, callback = undefined) => {
    const data = JSON.stringify({
      query: `mutation registerB2bCompany($input: RegisterB2bCompanyInput!){
                  registerB2bCompany( input: $input ){
                      companyId
                      keeper
                      error
                  }
              }`,
      variables: {
        input: {
          company: payload.company,
          contact: payload.contact,
          newsletter: !!payload.newsletter
        }
      }
    });

    const config = {
      method: 'post',
      url: adminurl,
      headers: {
        'Content-Type': 'application/json'
      },
      data
    };

    queue.push((queueCallback) => {
      http(config).then((response) => {
        if (response.data.errors) {
          triggerErrorLog(response.data.errors);
        }

        if (typeof callback === 'function') {
          if (!response.data?.data?.registerB2bCompany) {
            window.dispatchEvent(
              new CustomEvent('error-toggled', {
                detail: {
                  subtitle: '',
                  title: 'Error',
                  content: 'There was an internal error'
                }
              })
            );
          } else if (response.data.data.registerB2bCompany?.error?.length) {
            window.dispatchEvent(
              new CustomEvent('error-toggled', {
                detail: {
                  subtitle: '',
                  title: 'Error',
                  content: response.data.data.registerB2bCompany.error.join(',')
                }
              })
            );
          }
          callback(response.data);
        }
        queueCallback();
      });
    });
  };

  const validateVATNumber = async (value, market) => {
    const config = {
      method: 'post',
      url: `/wp-json/vat/v1/validate?country=${market}&vatNumber=${value}`,
      headers: {
        'Content-Type': 'application/json'
      }
    };

    try {
      const response = await http(config);
      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Hit error getting VAT');
    }
  };

  const checkDiscount = async (code, callback = undefined) => {
    const data = JSON.stringify({
      query: `mutation CheckDiscountCode( $code: String! ) {
          checkDiscountCode(input: { code: $code }) {
              valid
          }
        }`,
      variables: {
        code
      }
    });

    const config = {
      method: 'post',
      url: adminurl,
      headers: {
        'Content-Type': 'application/json'
      },
      data
    };

    queue.push((queueCallback) => {
      http(config).then((response) => {
        if (response.data.errors) {
          triggerErrorLog(response.data.errors);
        }

        if (typeof callback === 'function') {
          callback(response);
        }
        queueCallback();
      });
    });
  };

  return {
    events: () => events,
    forgotPassword,
    getSession,
    getSessionEmail,
    triggerAddToWishlist,
    getUser,
    getUserB2BInfo,
    login,
    loginEnabled,
    logout,
    register,
    removeAddress,
    updateAddress,
    setSession,
    showLoginOverlay,
    updateUser,
    addsB2BContact,
    queryDetachB2bUser,
    getB2BUser,
    getB2BUserOrderNextBatch,
    updateB2BContact,
    recommendProducts,
    forceRecommendProduct,
    einsteinProductRecommendations,
    einsteinCartRecommendations,
    registerB2bCompany,
    checkDiscount,
    validateVATNumber
  };
}

// eslint-disable-next-line func-names
const userService = (function () {
  const instance = new UserServiceConstructor();
  return {
    getInstance: () => instance
  };
})();

export default userService;
